import { SdkContext } from '../internal'

/* The member payloads are read by many screens with their own types; this resource does not narrow them yet. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApiPayload = any

/** The community's members (`/members`). The community is always the `X-CommunityId`. */
export class MembersResource {
  constructor(private ctx: SdkContext) {}

  async list(params?: {
    page?: number
    limit?: number
    search?: string
    role?: string
    /** Only members carrying this tag, matched without case. */
    tag?: string
    /** Several tags, comma-separated: carrying **any** of them is enough. */
    tags?: string
    /** Several group ids, comma-separated. Same rule, and it combines with
     *  `tags` by **and**: in one of these groups *and* carrying one of those
     *  tags. */
    groups?: string
    /** F-155: where the person is in the welcome. */
    welcome?: string
    sortBy?: string
    order?: 'asc' | 'desc'
    [key: string]: unknown
  }) {
    return this.ctx.call<ApiPayload[] | { items: ApiPayload[]; pagination: ApiPayload }>({ httpMethod: 'GET', path: '/members' }, params ? { query: params } : {})
  }

  /** The person's memberships in every community, each with its community. */
  async myCommunities(): Promise<ApiPayload[]> {
    return this.ctx.call({ httpMethod: 'GET', path: '/members/me/communities' })
  }

  async get(memberId: string): Promise<ApiPayload> {
    return this.ctx.call({ httpMethod: 'GET', path: `/members/{id}` }, { id: memberId })
  }

  /** F-177: the member page without login (`X-CommunityId` only); any "not here" is a 404. */
  async getPublic(memberId: string): Promise<ApiPayload> {
    return this.ctx.call({ httpMethod: 'GET', path: '/public/members/{id}' }, { id: memberId })
  }

  /** F-177: the person's posts and comments in spaces the viewer opens; `pagination.hidden` when the section is hidden. */
  async activity(memberId: string, params: { cursor?: string; limit?: number } = {}, signedOut = false): Promise<import('@/types/memberPage').MemberActivityPage> {
    const path = signedOut ? '/public/members/{id}/activity' : '/members/{id}/activity'
    const query = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null))
    const r = await this.ctx.call<ApiPayload>({ httpMethod: 'GET', path }, { id: memberId, query })
    return Array.isArray(r) ? { items: r } : { items: r?.items || [], pagination: r?.pagination }
  }

  /** F-177: the session's own page (company, country, languages, links, experience, privacy); only the keys sent change. */
  async updateMyPage(data: import('@/types/memberPage').MemberPageUpdate): Promise<ApiPayload> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/members/me/profile', hasBody: true }, { body: data })
  }

  async invite(data: { emails: string[]; role: string; [key: string]: unknown }) {
    return this.ctx.call({ httpMethod: 'POST', path: '/members/invite', hasBody: true }, { body: data })
  }

  async create(data: Record<string, unknown>): Promise<ApiPayload> {
    return this.ctx.call({ httpMethod: 'POST', path: '/members', hasBody: true }, { body: data })
  }

  async updateRole(memberId: string, data: { role: string }): Promise<ApiPayload> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: `/members/{id}/role`, hasBody: true },
      { id: memberId, body: data }
    )
  }

  /** F-166: the team's `reason` (up to 500) goes only to the community Logs; ignored when someone leaves on their own. */
  async remove(memberId: string, reason?: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/members/{id}`, hasBody: Boolean(reason) }, reason ? { id: memberId, body: { reason } } : { id: memberId })
  }

  /**
   * The session's own name, language and phone in this community. An empty phone clears it.
   * `certificatesVisibility` (F-154): who sees the person's certificates on the profile.
   */
  async updateMe(data: { name?: string; language?: string; phone?: string | null; phoneCountry?: string; certificatesVisibility?: 'team' | 'community' }): Promise<ApiPayload> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/members/me', hasBody: true }, { body: data })
  }

  async update(memberId: string, data: {
    /** `null` or empty clears this community's name: the account's name shows again. */
    displayName?: string | null
    tagline?: string
    /** Up to 100 characters; `null` or empty clears it. */
    jobTitle?: string | null
    [key: string]: unknown
  }): Promise<ApiPayload> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: `/members/{id}`, hasBody: true },
      { id: memberId, body: data }
    )
  }

  /** The member's login e-mail in this community. Owner and admin only. */
  async updateEmail(memberId: string, email: string): Promise<ApiPayload> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: `/members/{id}/email`, hasBody: true },
      { id: memberId, body: { email } }
    )
  }

  /**
   * The member's contact phone, normalized to E.164 by the server. An empty
   * string clears it; `phoneCountry` (ISO 3166-1 alpha-2) is the prefix when
   * the number comes without `+`. Owner and admin only.
   */
  async updatePhone(memberId: string, phone: string, phoneCountry?: string): Promise<ApiPayload> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: `/members/{id}/phone`, hasBody: true },
      { id: memberId, body: phoneCountry ? { phone, phoneCountry } : { phone } }
    )
  }

  /**
   * A member's manual tags. Replaces the entire list.
   *
   * `systemTags` does not go through here: the `speaker` tag is derived from
   * the submission history and has no write path — which is what keeps it from
   * becoming something granted or taken away by an admin's decision.
   */
  async updateTags(memberId: string, tags: string[]): Promise<ApiPayload> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: `/members/{id}/tags`, hasBody: true },
      { id: memberId, body: { tags } }
    )
  }

  /** The tags already used in the community, with counts — the suggestion list. */
  async listTags(): Promise<{ tag: string; count: number; color: string | null }[]> {
    const result = await this.ctx.raw<ApiPayload>({ httpMethod: 'GET', path: '/members/tags' })
    return Array.isArray(result) ? result : (result?.items ?? [])
  }

  /** Renames a tag everywhere. Renaming onto an existing one merges them. */
  async renameTag(tag: string, name: string): Promise<{ tag: string; membersUpdated: number }> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/members/tags/{tag}', hasBody: true },
      { tag, body: { name } }
    )
  }

  /** The colour this tag is drawn in, across the community. */
  async setTagColor(tag: string, color: string): Promise<{ tag: string; color: string }> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/members/tags/{tag}/color', hasBody: true },
      { tag, body: { color } }
    )
  }

  /** Takes a tag off every member in the community. */
  async deleteTag(tag: string): Promise<{ membersUpdated: number }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/members/tags/{tag}' }, { tag })
  }

  async autocomplete(search?: string): Promise<ApiPayload[]> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/members/autocomplete' },
      search ? { query: { search } } : {}
    )
  }
}
